import React from "react";
import { Equal, Users } from "lucide-react";
import CustomButton from "../ui/CustomButton";
import CurrencyIcon from "../ui/CurrencyIcon";
import { DEMO_AMOUNT_LABEL, formatCurrencyAmount, isAmountHidden, resolveDisplayCurrency } from "../../common/currencies";


const MatchCard = ({ match , onReveal }) => {
  const { game, details, result, amount, status, opponent, playerCount, entryFee, currencyCode, canReveal } = match;
  const displayCurrency = resolveDisplayCurrency(currencyCode);
  const amountValue = formatCurrencyAmount(amount, displayCurrency);
  const entryFeeValue = formatCurrencyAmount(entryFee, displayCurrency);
  const hideAmounts = isAmountHidden(currencyCode);
  const handleCardClick = () => {
    if (onReveal) {
      onReveal(match.registrationId);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "win":
      case "place":
        return "text-white";
      case "loss":
      case "tie":
      case "void":
        return "text-[var(--text-light)]";
      case "pending":
      case "reveal":
      case "waiting":
        return "text-yellow-500";
      default:
        return "text-white";
    }
  };

  const renderDetails = () => {
    if (opponent || playerCount || entryFee) {
      return (
        <div className="flex items-center gap-2 text-xs mt-0.5">
          {opponent && (
            <>
              <span className="text-[var(--text-dark)]">vs</span>
              <span className="text-[var(--text-light)]">{opponent}</span>
            </>
          )}
          {playerCount && (
            <>
              {opponent && <span className="text-[var(--text-dark)]">•</span>}
              <span className="text-white">{playerCount}</span>
              <Users className="w-3 h-3 text-[var(--text-dark)]" />
            </>
          )}
          {entryFee && (
            <span className="text-[var(--text-light)] flex items-center gap-0.5">
              {hideAmounts ? DEMO_AMOUNT_LABEL.entry : <><CurrencyIcon code={displayCurrency} className="h-3 w-3" />{entryFeeValue}</>} <span className="text-[var(--text-dark)]">Entry</span>
            </span>
          )}
        </div>
      );
    }

    // Fallback: parse the details string if it exists
    if (details) {
      return <span className="text-xs text-[var(--text-light)] mt-0.5">{details}</span>;
    }

    return null;
  };

  const renderRightSide = () => {
    if (canReveal) {
      return (
        <CustomButton
          color="green"
          onClick={(e) => {
            e.stopPropagation();
            onReveal?.(match.registrationId);
          }}
          className="rounded-full px-3 font-extrabold font-saira"
        >
          REVEAL
        </CustomButton>
      );
    }
    if (status === "pending" || status === "reveal" || status === "waiting" || status === "void") {
      return (
        <div className="flex flex-col items-end">
          <span className={`font-semibold text-sm mb-1 ${getStatusColor(status)}`}>
            {result || "—"}
          </span>
        </div>
      );
    }
    return (
      <>
        <div className={`text-sm font-semibold ${getStatusColor(status)}`}>
          {result}
        </div>
        {!hideAmounts && (
          <>
            <div
              className="w-[1px] h-10"
              style={{ background: 'linear-gradient(180deg, rgba(99, 99, 99, 0) 0%, rgba(99, 99, 99, 1) 48.56%, rgba(99, 99, 99, 0) 100%)' }}
            />

            <div className="min-w-[40px] text-center text-lg font-extrabold text-white font-saira">
              {status === 'tie' ? (
                <div className="flex justify-center"><Equal size={16} /></div>
              ) : (
                amount && Number(amount) !== 0 ? (
                  <span className="flex items-center justify-center gap-1">
                    <CurrencyIcon code={displayCurrency} className="h-4 w-4" symbolClassName="text-[var(--brand)]" />
                    {amountValue}
                  </span>
                ) : '--'
              )}
            </div>
          </>
        )}
      </>
    );
  };

  const getCardBackground = () => {
    const baseGradient = 'linear-gradient(180deg, #252525 0%, #1E1D1D 73.56%, #1E1D1D 100%)';

    if (canReveal) {
      return baseGradient;
    }

    if (status === 'win' || status === 'place') {
      return `linear-gradient(180deg, rgba(20, 53, 13, 0) 30%, #14350D 100%), ${baseGradient}`;
    } else if (status === 'loss') {
      return `linear-gradient(144.65deg, rgba(91, 23, 24, 0) 89%, rgb(91, 23, 24) 102.47%), ${baseGradient}`;
    } else {
      return baseGradient;
    }
  };

  const getBorderStyle = () => {
    if (status === 'win' || status === 'place' || status === 'pending' || status === 'reveal' || status === 'waiting') {
      return {
        borderTopWidth: '0.5px',
        borderTopStyle: 'solid',
        borderImageSource: 'linear-gradient(90deg, #121212 0%, #666666 53.85%, #121212 100%)',
        borderImageSlice: '1 0 0 0',
      };
    }
    return {};
  };

  return (
    <div
      onClick={canReveal ? undefined : (e) => handleCardClick(e)}
      className={`border border-neutral-800 rounded-app p-4 mb-3 flex items-center justify-between shadow-sm relative transition-colors ${canReveal ? "" : "cursor-pointer"}`}
      style={{
        background: getCardBackground(),
        ...getBorderStyle()
      }}
    >
      {(status === 'pending' || status === 'reveal' || status === 'waiting') && (
        <div className="absolute top-0 left-0 flex items-center gap-1">
          <div className="w-3 h-3 bg-[var(--brand)] rounded-full animate-pulse" />
        </div>
      )}

      <div className={`absolute left-0 top-0 bottom-0 w-1 overflow-hidden ${status === 'loss' ? 'bg-gradient-to-b from-red-500/20 to-transparent' : status === 'pending' || status === 'reveal' || status === 'waiting' ? 'bg-gradient-to-b from-yellow-500/20 to-transparent' : 'bg-gradient-to-b from-green-500/20 to-transparent'} opacity-0 transition-opacity`} />

      <div className="flex flex-col flex-1 min-w-0">
        <span className="font-semibold text-white text-base">{game}</span>
        {renderDetails()}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {renderRightSide()}
      </div>
    </div>
  );
};

export default MatchCard;
